import * as Sentry from "@sentry/react";
import { registerSW } from "@/lib/register-sw";
import { trpc } from "@/lib/trpc";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { UNAUTHED_ERR_MSG } from "@shared/const";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { useState } from "react";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";

const _sentryDsn = import.meta.env.VITE_SENTRY_DSN?.trim();
if (_sentryDsn) {
  Sentry.init({
    dsn: _sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

/** Umami only when both vars are set — avoids broken `%VITE_*%` literals in index.html when unset. */
function injectUmamiAnalytics() {
  const endpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT?.trim().replace(/\/$/, "");
  const websiteId = import.meta.env.VITE_ANALYTICS_WEBSITE_ID?.trim();
  if (!endpoint || !websiteId) return;
  if (document.querySelector("script[data-injected-umami]")) return;
  const script = document.createElement("script");
  script.defer = true;
  script.src = `${endpoint}/umami`;
  script.setAttribute("data-website-id", websiteId);
  script.setAttribute("data-injected-umami", "");
  document.body.appendChild(script);
}

injectUmamiAnalytics();
registerSW();

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

function TrpcWithClerk({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth();
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: "/api/trpc",
          transformer: superjson,
          async headers() {
            const token = await getToken();
            return token ? { Authorization: `Bearer ${token}` } : {};
          },
          fetch(url, opts) {
            return globalThis.fetch(url, {
              ...(opts ?? {}),
              credentials: "include",
            });
          },
        }),
      ],
    })
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      {children}
    </trpc.Provider>
  );
}

const root = createRoot(document.getElementById("root")!);

if (!publishableKey) {
  root.render(
    <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center text-sm text-muted-foreground">
      Missing <code className="mx-1">VITE_CLERK_PUBLISHABLE_KEY</code>. Add it
      to <code className="mx-1">.env</code> for local dev and in the Vercel
      project settings for production.
    </div>
  );
} else {
  root.render(
    <ClerkProvider publishableKey={publishableKey}>
      <TrpcWithClerk>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </TrpcWithClerk>
    </ClerkProvider>
  );
}
